import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';
import { AlertColor, Typography } from '@mui/material';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import strings from '../../../../common/Translation/Translate';
import Notification from '../../../../components/Notification';
import { FormDataModel } from '../../../../components/Form';
import axiosInstance from '../../../../utils/axios';
import { privileges } from '../../../../utils/constants/privileges';
import { routes } from '../../../../utils/constants/routes';
import { URLS } from '../../../../utils/constants/urls';
import { Privilege } from '../../../../utils/redux/reducer/authentication-slice';
import {
  filterDataforApi,
  pageNo,
  rowCount,
  setFilter,
  setFilterDataForApi,
  setPageNo,
} from 'utils/redux/reducer/filter-slice';
import ListPage from '../../CommonPage';
import MasterDataEntityDrawer from '../../CommonPage/MasterDataEntityDrawer';
import { lastUpdatedTableColumn } from '../../CommonPage/lastUpdatedColumn';
import { AddRoleForm } from './AddEditRole/AddRoleForm';

const RoleList: FC = () => {
  const dispatch = useDispatch();
  const index = useSelector(pageNo);
  const filter = useSelector(filterDataforApi);
  const pageSize = useSelector(rowCount);
  const userPrivileges = useSelector(Privilege) as string[];
  const navigate = useNavigate();

  const [pageResponse, setPageResponse] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formValues, setFormValues] = useState<FormDataModel>({});
  const [message, setMessage] = useState<{ display: boolean; severity: AlertColor | null; message: string }>({
    display: false,
    severity: null,
    message: '',
  });

  const formModel = useMemo(
    () => AddRoleForm(strings, 4).map(field => ({ ...field, responsive: { xs: 12 }, required: true })),
    [],
  );

  const closeNotification = (value: boolean) => setMessage(prev => ({ ...prev, display: value }));

  const handleCatch = (error: any) => {
    setMessage({
      display: true,
      severity: 'error',
      message: error?.response?.data?.error_description ?? error?.response?.data?.message ?? error?.message,
    });
  };

  const getList = useCallback(
    async (filterData?: any) => {
      try {
        setLoading(true);
        const body = filterData
          ? filterData
          : { page: index + 1, size: pageSize, filter: filter ?? [], sort: ['createdAt:desc'] };
        const { data } = await axiosInstance.post(URLS.rolePage, body);
        setPageResponse(data);
      } catch (error: any) {
        handleCatch(error);
      } finally {
        setLoading(false);
      }
    },
    [index, pageSize, filter],
  );

  useEffect(() => {
    getList(null);
  }, [getList]);

  const openDrawer = useCallback(async (recordId: number | null) => {
    setEditingId(recordId);
    if (recordId) {
      try {
        setDrawerLoading(true);
        const { data } = await axiosInstance.get(URLS.editRole(String(recordId)));
        setFormValues({ name: data?.name ?? '' });
      } catch (error: any) {
        handleCatch(error);
        return;
      } finally {
        setDrawerLoading(false);
      }
    } else {
      setFormValues({ name: '' });
    }
    setDrawerOpen(true);
  }, []);

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditingId(null);
    setFormValues({});
  };

  const handleSave = async (body: FormDataModel) => {
    try {
      setSaving(true);
      const payload = { name: body.name };
      if (editingId) {
        await axiosInstance.put(URLS.editRole(String(editingId)), payload);
        setMessage({ display: true, severity: 'success', message: strings.common_update_message });
        closeDrawer();
        getList(null);
      } else {
        const { data } = await axiosInstance.post(URLS.addRole, payload);
        setMessage({ display: true, severity: 'success', message: strings.common_save_message });
        closeDrawer();
        if (data?.id && userPrivileges?.includes(privileges.edit_privileges)) {
          navigate(`${routes.privilige}/${data.id}`);
        } else {
          getList(null);
        }
      }
    } catch (error: any) {
      handleCatch(error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editingId) return;
    try {
      setSaving(true);
      await axiosInstance.delete(URLS.deleteRole(String(editingId)));
      setMessage({ display: true, severity: 'success', message: strings.common_delete_message });
      closeDrawer();
      getList(null);
    } catch (error: any) {
      handleCatch(error);
    } finally {
      setSaving(false);
    }
  };

  const pageConfig = useMemo(
    () => ({
      title: strings.rolesmgmt_title_roles,
      subtitle: strings.roles_list_subtitle,
      searchField: 'name',
      searchPlaceholder: 'Search roles',
      AddButtonText: strings.rolesmgmt_button_add,
      listApi: URLS.rolePage,
      deleteApi: URLS.deleteRole,
      addPrivilege: privileges.add_role,
      editPrivilege: privileges.edit_role,
      deletePrivilege: privileges.delete_role,
      customAdd: () => openDrawer(null),
      customEdit: (id: number) => openDrawer(id),
      inlineCustomActions: true,
      actionMenuItems: {
        materialIcon: <VpnKeyOutlinedIcon fontSize="small" color="primary" />,
        buttonText: strings.rolesmgmt_button_privileges || 'Privileges',
        privilege: privileges.edit_privileges,
        handleCustomImageMethod: (id: number) => navigate(`${routes.privilige}/${id}`),
      },
      tableColumn: [
        {
          field: 'name',
          headerName: strings.rolesmgmt_column_name,
          flex: 2.5,
          sortable: true,
          renderCell: (value: string) => (
            <Typography sx={{ fontWeight: 600, fontSize: '0.875rem' }}>{value || '—'}</Typography>
          ),
        },
        {
          field: 'userCount',
          headerName: 'Users',
          flex: 1.2,
          sortable: false,
          renderCell: (_value: any, row: any) => {
            const count = Number(row.userCount ?? 0);
            const label = count === 1 ? '1 User' : `${count} Users`;
            return (
              <Typography
                component="span"
                role="button"
                color="primary"
                sx={{
                  fontSize: '0.8125rem',
                  fontWeight: 700,
                  lineHeight: 1.35,
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  textUnderlineOffset: '2px',
                }}
                onClick={e => {
                  e.stopPropagation();
                  if (!row?.name) return;
                  dispatch(setPageNo(0));
                  dispatch(setFilter({ role: row.name }));
                  dispatch(setFilterDataForApi([`role:cic:${row.name}`]));
                  navigate(`${routes.users}?role=${encodeURIComponent(row.name)}`);
                }}>
                {label}
              </Typography>
            );
          },
        },
        {
          field: 'privilegeCount',
          headerName: 'Privileges',
          flex: 1.2,
          sortable: false,
          renderCell: (_value: any, row: any) => {
            const selected = Number(row.privilegeCount ?? 0);
            const total = Number(row.totalPrivilegeCount ?? 0);
            return (
              <Typography sx={{ fontSize: '0.8125rem', fontWeight: 700, color: 'primary.main', lineHeight: 1.35 }}>
                {selected}/{total}
              </Typography>
            );
          },
        },
        lastUpdatedTableColumn(),
      ],
    }),
    [navigate, openDrawer],
  );

  return (
    <>
      {message.display && (
        <Notification
          isOpen={message.display}
          message={message.message}
          severity={message.severity as AlertColor}
          closeNotification={closeNotification}
        />
      )}
      <ListPage isLoading={loading} pageConfig={pageConfig} pageResponse={pageResponse} getList={getList} />
      <MasterDataEntityDrawer
        open={drawerOpen}
        title={editingId ? strings.rolesmgmt_title_edit : strings.roles_title_add}
        isEdit={Boolean(editingId)}
        formModel={formModel}
        formValues={formValues}
        loading={drawerLoading}
        saving={saving}
        canDelete={userPrivileges?.includes(privileges.delete_role)}
        deleteEntityName={String(formValues.name || '')}
        onClose={closeDrawer}
        onSave={handleSave}
        onDelete={handleDelete}
        onInvalid={msg => setMessage({ display: true, severity: 'error', message: msg })}
      />
    </>
  );
};

export default RoleList;
